import React from 'react'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { Input } from '@/components/ui/input'
import { Search, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { sdk } from '@/lib/sdk'
import { imageUrl, imageSrcSet } from '@/lib/images'
import { reloadPage } from '@/lib/player/leave'

/** Avatar square, cropped on the face, at the size it is drawn. */
const AVATAR = (size: number) =>
  ({ w: size, h: size, fit: 'crop' as const, crop: 'faces' })


interface MemberOption {
  memberId: string
  displayName?: string | null
  fullName?: string | null
  email?: string | null
  avatarUrl?: string | null
  role: string
}

/** Select value for "act as myself": Radix refuses an empty-string item value. */
const SELF = '__self__'

export const MemberSelector = () => {
  const { t } = useTranslation('navigation')
  const { user } = useAuth()
  const { community } = useCommunity()
  const [members, setMembers] = React.useState<MemberOption[]>([])
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedTerm, setDebouncedTerm] = React.useState('')
  const [selectedMember, setSelectedMember] = React.useState<string>('')
  const [isInitialized, setIsInitialized] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  React.useEffect(() => {
    const fetchMembers = async () => {
      try {
        const data = await sdk.members.list()
        if (data) {
          const membersData: MemberOption[] = Array.isArray(data)
            ? data
            : (data as { items?: MemberOption[] }).items || []
          setMembers(membersData)

          /* Never picks a member on its own: a SuperAdmin acts as themselves
             (no `X-MemberId`) until they choose one here. This used to select
             the first owner and start impersonating on mount. It only reflects
             a choice already made — the AuthContext drops a stored id chosen
             in another community before this runs. */
          if (!isInitialized) {
            const storedMemberId = sdk.getCurrentMember()
            const storedHere = sdk.getCurrentMemberCommunity() === community?.id
            if (storedMemberId && storedHere && membersData.some(m => m.memberId === storedMemberId)) {
              setSelectedMember(storedMemberId)
            }
            setIsInitialized(true)
          }
        }
      } catch {
        /* No list, no picker: it renders nothing without members. */
      }
    }

    if (user?.isSuperAdmin && community) {
      fetchMembers()
    }
  }, [user, community, isInitialized])

  const filteredMembers = React.useMemo(() => {
    const searchLower = debouncedTerm.toLowerCase()
    return members.filter(member =>
      (member.displayName || member.fullName || '').toLowerCase().includes(searchLower) ||
      (member.email || '').toLowerCase().includes(searchLower)
    )
  }, [members, debouncedTerm])

  /** An explicit choice — the only way `X-MemberId` gets set. `SELF` clears it. */
  const handleMemberChange = (memberId: string) => {
    const chosen = memberId === SELF ? '' : memberId
    setSelectedMember(chosen)
    sdk.setMemberId(chosen)
    reloadPage()
  }

  const getSelectedMemberDisplay = () => {
    const member = members.find(m => m.memberId === selectedMember)
    if (!member) return null

    return (
      <div className="flex items-center gap-2">
        {member.avatarUrl ? (
          <img
            src={imageUrl(member.avatarUrl, AVATAR(24)) || undefined}
            srcSet={imageSrcSet(member.avatarUrl, AVATAR(24))}
            alt=""
            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-gray-400" />
          </div>
        )}
        <span className="truncate">
          {member.displayName || member.fullName} ({member.role})
        </span>
      </div>
    )
  }

  if (!user?.isSuperAdmin || !members.length) {
    return null
  }

  return (
    <Select value={selectedMember || SELF} onValueChange={handleMemberChange}>
      <SelectTrigger className="w-[200px] bg-white/60 backdrop-blur-sm border-white/50">
        <SelectValue>
          {selectedMember && getSelectedMemberDisplay() ? (
            getSelectedMemberDisplay()
          ) : (
            t('actAsMyself')
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="w-[300px] p-0 z-[150]">
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchTerm('')
                }
              }}
              placeholder={t('searchMembers')}
              className="pl-8"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto py-1">
          <SelectItem
            value={SELF}
            className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 outline-none"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-sm font-medium truncate">{t('actAsMyself')}</span>
            </div>
          </SelectItem>
          {filteredMembers.map((member) => (
            <SelectItem 
              key={member.memberId} 
              value={member.memberId}
              className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 outline-none"
            >
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0">
                  {member.avatarUrl ? (
                    <img
                      src={imageUrl(member.avatarUrl, AVATAR(24)) || undefined}
                      srcSet={imageSrcSet(member.avatarUrl, AVATAR(24))}
                      alt={member.displayName || member.fullName || ''}
                      loading="lazy"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium truncate">
                    {member.displayName || member.fullName} ({member.role})
                  </span>
                  <span className="text-xs text-gray-500 truncate">{member.email}</span>
                </div>
              </div>
            </SelectItem>
          ))}
        </div>
      </SelectContent>
    </Select>
  )
}